// src/components/Dashboard/BlogAdmin.jsx
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiBookOpen, FiImage } from 'react-icons/fi';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import './BlogAdmin.css';
import { db } from '../firebase/firebase-config';

function BlogAdmin() {
  const [blogs, setBlogs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlog, setCurrentBlog] = useState({
    firebaseId: '',
    title: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    image: '', 
    summary: '',
    content: ''
  });

  // Firestore'dan makaleleri çekiyoruz
  useEffect(() => {
    const q = query(collection(db, 'blogs'), orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const blogList = snapshot.docs.map(docSnap => ({
        firebaseId: docSnap.id,
        ...docSnap.data()
      }));
      setBlogs(blogList);
    }, (error) => {
      console.error("Makaleler yüklenirken hata oluştu:", error);
    });

    return () => unsubscribe();
  }, []);

  // Dosya seçildiğinde performans için görseli optimize ederek okuma
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 1 MB'dan büyük fotoğraflar için kullanıcıyı uyarabiliriz veya canvas ile küçültebiliriz
      const reader = new FileReader();
      reader.onloadend = () => {
        // Görsel kalitesini korurken boyutu optimize etmek için Image nesnesi kullanabiliriz
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800; // Genişliği sınırlandırıyoruz kanka
          const MAX_HEIGHT = 600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Sıkıştırılmış base64 verisini alıyoruz
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          setCurrentBlog({ ...currentBlog, image: compressedDataUrl });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  // Düzenleme modunu açma
  const handleEditClick = (blog) => {
    setIsEditing(true);
    setCurrentBlog({
      firebaseId: blog.firebaseId,
      title: blog.title || '',
      category: blog.category || '',
      date: blog.date || new Date().toISOString().split('T')[0],
      image: blog.image || '',
      summary: blog.summary || '',
      content: blog.content || ''
    });
  };

  // Yeni ekleme modu açma
  const handleAddNew = () => {
    setIsEditing(true);
    setCurrentBlog({
      firebaseId: '',
      title: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      image: '',
      summary: '',
      content: ''
    });
  };

  // Kaydetme (Firebase'e Ekleme veya Güncelleme)
  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (currentBlog.firebaseId) {
        // Güncelleme
        const blogRef = doc(db, 'blogs', currentBlog.firebaseId);
        await updateDoc(blogRef, {
          title: currentBlog.title,
          category: currentBlog.category,
          date: currentBlog.date,
          image: currentBlog.image,
          summary: currentBlog.summary,
          content: currentBlog.content
        });
      } else {
        // Yeni Ekleme
        const customSlug = currentBlog.title.toLowerCase().replace(/ /g, '-').replace(/[ığüşöç]/g, (v) => {
          return {'ı':'i', 'ğ':'g', 'ü':'u', 'ş':'s', 'ö':'o', 'ç':'c'}[v];
        });

        await addDoc(collection(db, 'blogs'), {
          id: customSlug,
          title: currentBlog.title,
          category: currentBlog.category,
          date: currentBlog.date,
          image: currentBlog.image,
          summary: currentBlog.summary,
          content: currentBlog.content
        });
      }

      setIsEditing(false);
      setCurrentBlog({
        firebaseId: '',
        title: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        image: '',
        summary: '',
        content: ''
      });
    } catch (error) {
      console.error("Kayıt sırasında hata oluştu:", error);
      alert("Kayıt sırasında hata oluştu kanka. Görsel çok büyük olabilir, daha küçük boyutlu bir fotoğraf seçmeyi dene.");
    }
  };

  // Silme İşlemi
  const handleDelete = async (firebaseId) => {
    if (window.confirm("Bu makaleyi silmek istediğinize emin misiniz?")) {
      try {
        await deleteDoc(doc(db, 'blogs', firebaseId));
      } catch (error) {
        console.error("Makale silinirken hata oluştu:", error);
      }
    }
  };

  return (
    <div className="blog-admin-container">
      <div className="admin-section-header">
        <h3><FiBookOpen /> Blog ve Makale Yönetimi</h3>
        <button className="add-btn" onClick={handleAddNew}>
          <FiPlus /> Yeni Makale Ekle
        </button>
      </div>

      {/* Ekleme / Düzenleme Formu */}
      {isEditing && (
        <form onSubmit={handleSave} className="blog-form-card">
          <h4>{currentBlog.firebaseId ? 'Makaleyi Düzenle' : 'Yeni Makale Ekle'}</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Makale Başlığı *</label>
              <input 
                type="text" 
                placeholder="Örn: Boşanma Davalarında Tazminat" 
                value={currentBlog.title}
                onChange={(e) => setCurrentBlog({ ...currentBlog, title: e.target.value })}
                required 
              />
            </div>

            <div className="form-row-admin">
              <div className="form-group">
                <label>Kategori *</label>
                <input 
                  type="text" 
                  placeholder="Örn: Aile Hukuku" 
                  value={currentBlog.category}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, category: e.target.value })}
                  required 
                />
              </div>

              <div className="form-group">
                <label>Yayın Tarihi *</label>
                <input 
                  type="date" 
                  value={currentBlog.date}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, date: e.target.value })}
                  required 
                />
              </div>
            </div>

            <div className="form-group">
              <label><FiImage /> Kapak Fotoğrafı Seç (Bilgisayardan)</label>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
                style={{ padding: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'var(--text-main)' }}
              />
              {currentBlog.image && (
                <div style={{ marginTop: '10px' }}>
                  <img src={currentBlog.image} alt="Önizleme" style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--gold-primary)' }} />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Kısa Özet (Kartlarda Görünen) *</label>
              <textarea 
                placeholder="Makalenin kısa özeti..." 
                value={currentBlog.summary}
                onChange={(e) => setCurrentBlog({ ...currentBlog, summary: e.target.value })}
                rows="2"
                required 
              ></textarea>
            </div>

            <div className="form-group">
              <label>Makale İçeriği *</label>
              <textarea 
                placeholder="Makalenin tam metni..." 
                value={currentBlog.content}
                onChange={(e) => setCurrentBlog({ ...currentBlog, content: e.target.value })}
                rows="6"
                required 
              ></textarea>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-btn"><FiSave /> Kaydet</button>
            <button type="button" className="cancel-btn" onClick={() => setIsEditing(false)}><FiX /> İptal</button>
          </div>
        </form>
      )}

      {/* Kayıtlı Makaleler Listesi */}
      <div className="blogs-list-grid">
        {blogs.length === 0 ? (
          <p className="no-blogs-text" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>
            Henüz hiç makale eklenmedi. Yukarıdaki butona tıklayarak ilk makaleyi ekleyebilirsin kanka.
          </p>
        ) : (
          blogs.map((blog) => (
            <div key={blog.firebaseId} className="admin-blog-card">
              <div className="blog-info" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {blog.image && (
                  <img src={blog.image} alt={blog.title} style={{ width: '60px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(197,160,89,0.3)' }} />
                )}
                <div>
                  <h4>{blog.title}</h4>
                  <p><strong>Kategori:</strong> {blog.category} | <strong>Tarih:</strong> {blog.date}</p>
                  <p className="blog-summary-preview">{blog.summary}</p>
                  <span className="blog-id-badge">ID: {blog.id || blog.firebaseId}</span>
                </div>
              </div>
              <div className="blog-actions">
                <button className="edit-btn" onClick={() => handleEditClick(blog)} title="Düzenle">
                  <FiEdit2 />
                </button>
                <button className="delete-btn" onClick={() => handleDelete(blog.firebaseId)} title="Sil">
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default BlogAdmin;